/**
 * @sazeo/sdk — Expenses hooks
 *
 * Hooks that call the expenses Edge Function handlers. All requests carry the
 * caller's Supabase JWT so RLS enforces tenant isolation automatically.
 *
 * Paired spec: specs/CODEX_EXPENSES_ADDON.md
 */

import { type TenantSupabaseClient, getTenantClient } from '@sazeo/supabase-tenant';
import { useCallback, useEffect, useRef, useState } from 'react';

// ---------------------------------------------------------------------------
// Row types
// ---------------------------------------------------------------------------

export type ClaimStatus = 'draft' | 'submitted' | 'approved' | 'rejected' | 'paid';

export interface ExpenseCategory {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  created_at: string;
}

export interface ExpenseClaim {
  id: string;
  tenant_id: string;
  user_id: string;
  category_id: string | null;
  claim_no: string;
  title: string;
  amount: number;
  currency: string;
  expense_date: string;
  receipt_url: string | null;
  notes: string | null;
  status: ClaimStatus;
  reviewed_by: string | null;
  reviewed_at: string | null;
  paid_at: string | null;
  rejection_reason: string | null;
  created_at: string;
  updated_at: string;
}

export interface ExpenseClaimFilters {
  userId?: string;
  status?: ClaimStatus;
  dateFrom?: string;
  dateTo?: string;
}

export interface ExpenseSummary {
  submittedCount: number;
  approvedCount: number;
  pendingAmount: number;
  currency: string;
}

export interface CreateExpenseClaimInput {
  title: string;
  category_id?: string | null;
  amount: number;
  currency?: string;
  expense_date: string;
  receipt_url?: string | null;
  notes?: string | null;
}

export interface ReviewExpenseClaimInput {
  status: 'approved' | 'rejected';
  reviewerNote?: string;
  rejectionReason?: string;
}

// ---------------------------------------------------------------------------
// Internal fetch helpers (same pattern as tasks.ts)
// ---------------------------------------------------------------------------

interface SupabaseAuthLike {
  auth: {
    getSession: () => Promise<{ data: { session: { access_token: string } | null } }>;
  };
}

function resolveClient(supabase: unknown): SupabaseAuthLike {
  if (supabase) return supabase as SupabaseAuthLike;
  try {
    return getTenantClient() as unknown as TenantSupabaseClient as unknown as SupabaseAuthLike;
  } catch {
    return { auth: { getSession: async () => ({ data: { session: null } }) } };
  }
}

async function getAuthHeaders(supabase: unknown): Promise<Record<string, string>> {
  const client = resolveClient(supabase);
  const { data } = await client.auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function apiFetch<T>(supabase: unknown, path: string, init?: RequestInit): Promise<T> {
  const headers = await getAuthHeaders(supabase);
  const res = await fetch(`/functions/v1/${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...headers, ...init?.headers },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
      error?: { message?: string };
    };
    throw new Error(body.error?.message ?? res.statusText);
  }
  if (res.status === 204) return {} as T;
  return res.json() as Promise<T>;
}

function useAsyncQuery<T>(
  fn: () => Promise<T>,
  initial: T,
  deps: readonly unknown[],
  enabled = true,
): { data: T; isLoading: boolean; error: Error | null; refetch: () => void } {
  const [data, setData] = useState<T>(initial);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const run = useCallback(() => {
    if (!enabled) return;
    setIsLoading(true);
    setError(null);
    fn()
      .then((d) => {
        if (mountedRef.current) setData(d);
      })
      .catch((e) => {
        if (mountedRef.current) setError(e instanceof Error ? e : new Error(String(e)));
      })
      .finally(() => {
        if (mountedRef.current) setIsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, isLoading, error, refetch: run };
}

// ---------------------------------------------------------------------------
// Query builders
// ---------------------------------------------------------------------------

function buildExpenseClaimQuery(tenantId: string, filters?: ExpenseClaimFilters): string {
  const params = new URLSearchParams({ tenant_id: tenantId });
  if (filters?.userId) params.set('user_id', filters.userId);
  if (filters?.status) params.set('status', filters.status);
  if (filters?.dateFrom) params.set('date_from', filters.dateFrom);
  if (filters?.dateTo) params.set('date_to', filters.dateTo);
  return `addons/expenses/claims?${params.toString()}`;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useExpenseCategories(supabase: unknown, tenantId: string) {
  return useAsyncQuery<ExpenseCategory[]>(
    () => apiFetch<ExpenseCategory[]>(supabase, `addons/expenses/categories?tenant_id=${tenantId}`),
    [],
    [tenantId],
    Boolean(tenantId),
  );
}

export function useExpenseClaims(
  supabase: unknown,
  tenantId: string,
  filters?: ExpenseClaimFilters,
) {
  const keyParts: readonly unknown[] = [
    tenantId,
    filters?.userId ?? null,
    filters?.status ?? null,
    filters?.dateFrom ?? null,
    filters?.dateTo ?? null,
  ];
  return useAsyncQuery<ExpenseClaim[]>(
    () => apiFetch<ExpenseClaim[]>(supabase, buildExpenseClaimQuery(tenantId, filters)),
    [],
    keyParts,
    Boolean(tenantId),
  );
}

export function useMyExpenseSummary(supabase: unknown, tenantId: string, userId: string) {
  return useAsyncQuery<ExpenseSummary | null>(
    async () => {
      // Fetch current month's claims for this user and derive summary client-side.
      const month = new Date().toISOString().slice(0, 7);
      const dateFrom = `${month}-01`;
      const dateTo = `${month}-31`;
      const claims = await apiFetch<ExpenseClaim[]>(
        supabase,
        buildExpenseClaimQuery(tenantId, { userId, dateFrom, dateTo }),
      );
      const currency = claims[0]?.currency ?? 'GEL';
      const submittedCount = claims.filter((c) => c.status === 'submitted').length;
      const approvedCount = claims.filter((c) => c.status === 'approved').length;
      const pendingAmount = claims
        .filter((c) => c.status === 'submitted' || c.status === 'approved')
        .reduce((sum, c) => sum + Number(c.amount), 0);
      return { submittedCount, approvedCount, pendingAmount, currency };
    },
    null,
    [tenantId, userId],
    Boolean(tenantId) && Boolean(userId),
  );
}

export function useCreateExpenseClaim(supabase: unknown) {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = useCallback(
    async (input: { tenantId: string } & CreateExpenseClaimInput) => {
      setIsPending(true);
      setError(null);
      try {
        return await apiFetch<ExpenseClaim>(supabase, 'addons/expenses/claims', {
          method: 'POST',
          body: JSON.stringify(input),
        });
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setIsPending(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return { mutate, isPending, error };
}

export function useSubmitExpenseClaim(supabase: unknown) {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = useCallback(
    async (input: { tenantId: string; claimId: string; userId: string }) => {
      setIsPending(true);
      setError(null);
      try {
        return await apiFetch<ExpenseClaim>(
          supabase,
          `addons/expenses/claims/${input.claimId}/submit`,
          { method: 'POST', body: JSON.stringify({ tenant_id: input.tenantId }) },
        );
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setIsPending(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return { mutate, isPending, error };
}

export function useReviewExpenseClaim(supabase: unknown) {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = useCallback(
    async (input: {
      tenantId: string;
      claimId: string;
      review: ReviewExpenseClaimInput;
      reviewerId: string;
    }) => {
      setIsPending(true);
      setError(null);
      try {
        return await apiFetch<ExpenseClaim>(
          supabase,
          `addons/expenses/claims/${input.claimId}/review`,
          {
            method: 'PATCH',
            body: JSON.stringify({
              tenant_id: input.tenantId,
              status: input.review.status,
              rejection_reason: input.review.reviewerNote ?? input.review.rejectionReason ?? null,
            }),
          },
        );
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setIsPending(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return { mutate, isPending, error };
}

export function useMarkExpensePaid(supabase: unknown) {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = useCallback(
    async (input: { tenantId: string; claimId: string }) => {
      setIsPending(true);
      setError(null);
      try {
        return await apiFetch<ExpenseClaim>(
          supabase,
          `addons/expenses/claims/${input.claimId}/mark-paid`,
          { method: 'POST', body: JSON.stringify({ tenant_id: input.tenantId }) },
        );
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setIsPending(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return { mutate, isPending, error };
}

// ---------------------------------------------------------------------------
// L85: Core Expense Reports (via Supabase direct — no addon wrapper)
// ---------------------------------------------------------------------------

export type ExpenseReportStatus =
  | 'draft'
  | 'submitted'
  | 'approved'
  | 'rejected'
  | 'reimbursed'
  | 'cancelled';

export type ExpenseItemStatus = 'pending' | 'approved' | 'rejected';

export type ExpenseItemCategory =
  | 'meals'
  | 'travel'
  | 'accommodation'
  | 'office_supplies'
  | 'other';

export interface ExpenseReport {
  id: string;
  tenant_id: string;
  employee_id: string;
  status: ExpenseReportStatus;
  period_start: string;
  period_end: string;
  currency: string;
  total_amount: number;
  submitted_at: string | null;
  approved_at: string | null;
  approved_by: string | null;
  rejected_at: string | null;
  rejected_by: string | null;
  reimbursed_at: string | null;
  reimbursement_reference: string | null;
  created_at: string;
  updated_at: string;
}

export interface ExpenseItem {
  id: string;
  tenant_id: string;
  report_id: string;
  category: ExpenseItemCategory;
  description: string;
  amount: number;
  expense_date: string;
  receipt_url: string | null;
  status: ExpenseItemStatus;
  rejection_reason: string | null;
  created_at: string;
}

export interface ExpenseApproval {
  id: string;
  tenant_id: string;
  report_id: string;
  approver_id: string;
  action: 'approved' | 'rejected' | 'commented';
  comments: string | null;
  created_at: string;
}

// Query hooks
export function useExpenseReportsForEmployee(
  supabase: unknown,
  tenantId: string,
  employeeId: string,
) {
  return useAsyncQuery<ExpenseReport[]>(
    async () =>
      apiFetch<ExpenseReport[]>(
        supabase,
        `expenses/reports?tenant_id=${tenantId}&employee_id=${employeeId}`,
      ),
    [],
    [tenantId, employeeId],
    Boolean(tenantId) && Boolean(employeeId),
  );
}

export function useExpenseReportsForApprover(supabase: unknown, tenantId: string) {
  return useAsyncQuery<ExpenseReport[]>(
    async () =>
      apiFetch<ExpenseReport[]>(
        supabase,
        `expenses/reports/pending-approval?tenant_id=${tenantId}`,
      ),
    [],
    [tenantId],
    Boolean(tenantId),
  );
}

export function useApprovedExpenseReports(supabase: unknown, tenantId: string) {
  return useAsyncQuery<ExpenseReport[]>(
    async () =>
      apiFetch<ExpenseReport[]>(supabase, `expenses/reports/approved?tenant_id=${tenantId}`),
    [],
    [tenantId],
    Boolean(tenantId),
  );
}

export function useExpenseItemsForReport(supabase: unknown, tenantId: string, reportId: string) {
  return useAsyncQuery<ExpenseItem[]>(
    async () =>
      apiFetch<ExpenseItem[]>(supabase, `expenses/reports/${reportId}/items?tenant_id=${tenantId}`),
    [],
    [reportId, tenantId],
    Boolean(reportId) && Boolean(tenantId),
  );
}

export function useExpenseApprovalsForReport(
  supabase: unknown,
  tenantId: string,
  reportId: string,
) {
  return useAsyncQuery<ExpenseApproval[]>(
    async () =>
      apiFetch<ExpenseApproval[]>(
        supabase,
        `expenses/reports/${reportId}/approvals?tenant_id=${tenantId}`,
      ),
    [],
    [reportId, tenantId],
    Boolean(reportId) && Boolean(tenantId),
  );
}

export function useExpenseHistory(supabase: unknown, tenantId: string, reportId: string) {
  return useAsyncQuery<
    Array<{
      timestamp: string;
      action: string;
      actor: string;
      details?: string;
    }>
  >(
    async () => apiFetch(supabase, `expenses/reports/${reportId}/history?tenant_id=${tenantId}`),
    [],
    [reportId, tenantId],
    Boolean(reportId) && Boolean(tenantId),
  );
}

// Mutation hooks
export function useCreateExpenseReport(supabase: unknown) {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = useCallback(
    async (input: {
      tenantId: string;
      period_start: string;
      period_end: string;
      currency?: string;
    }) => {
      setIsPending(true);
      setError(null);
      try {
        return await apiFetch<ExpenseReport>(supabase, 'expenses/reports', {
          method: 'POST',
          body: JSON.stringify(input),
        });
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setIsPending(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return { mutate, isPending, error };
}

export function useAddExpenseItem(supabase: unknown) {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = useCallback(
    async (input: {
      tenantId: string;
      report_id: string;
      category: ExpenseItemCategory;
      description: string;
      amount: number;
      expense_date: string;
      receipt_url?: string;
    }) => {
      setIsPending(true);
      setError(null);
      try {
        return await apiFetch<ExpenseItem>(supabase, `expenses/reports/${input.report_id}/items`, {
          method: 'POST',
          body: JSON.stringify(input),
        });
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setIsPending(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return { mutate, isPending, error };
}

export function useUpdateExpenseItem(supabase: unknown) {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = useCallback(
    async (input: {
      tenantId: string;
      item_id: string;
      category?: ExpenseItemCategory;
      description?: string;
      amount?: number;
      expense_date?: string;
      receipt_url?: string;
    }) => {
      setIsPending(true);
      setError(null);
      try {
        return await apiFetch<ExpenseItem>(supabase, `expenses/items/${input.item_id}`, {
          method: 'PATCH',
          body: JSON.stringify(input),
        });
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setIsPending(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return { mutate, isPending, error };
}

export function useDeleteExpenseItem(supabase: unknown) {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = useCallback(
    async (input: { tenantId: string; item_id: string }) => {
      setIsPending(true);
      setError(null);
      try {
        await apiFetch(supabase, `expenses/items/${input.item_id}`, {
          method: 'DELETE',
          body: JSON.stringify(input),
        });
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setIsPending(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return { mutate, isPending, error };
}

export function useSubmitExpenseReport(supabase: unknown) {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = useCallback(
    async (input: { tenantId: string; report_id: string }) => {
      setIsPending(true);
      setError(null);
      try {
        return await apiFetch<ExpenseReport>(
          supabase,
          `expenses/reports/${input.report_id}/submit`,
          {
            method: 'POST',
            body: JSON.stringify(input),
          },
        );
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setIsPending(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return { mutate, isPending, error };
}

export function useApproveExpenseReport(supabase: unknown) {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = useCallback(
    async (input: {
      tenantId: string;
      report_id: string;
      comments?: string;
    }) => {
      setIsPending(true);
      setError(null);
      try {
        return await apiFetch<ExpenseReport>(
          supabase,
          `expenses/reports/${input.report_id}/approve`,
          {
            method: 'POST',
            body: JSON.stringify(input),
          },
        );
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setIsPending(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return { mutate, isPending, error };
}

export function useRejectExpenseReport(supabase: unknown) {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = useCallback(
    async (input: { tenantId: string; report_id: string; reason: string }) => {
      setIsPending(true);
      setError(null);
      try {
        return await apiFetch<ExpenseReport>(
          supabase,
          `expenses/reports/${input.report_id}/reject`,
          {
            method: 'POST',
            body: JSON.stringify(input),
          },
        );
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setIsPending(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return { mutate, isPending, error };
}

export function useMarkExpenseReimbursed(supabase: unknown) {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = useCallback(
    async (input: {
      tenantId: string;
      report_id: string;
      reference: string;
    }) => {
      setIsPending(true);
      setError(null);
      try {
        return await apiFetch<ExpenseReport>(
          supabase,
          `expenses/reports/${input.report_id}/reimburse`,
          {
            method: 'POST',
            body: JSON.stringify(input),
          },
        );
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setIsPending(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return { mutate, isPending, error };
}

export function useBulkMarkReimbursed(supabase: unknown) {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = useCallback(
    async (input: {
      tenantId: string;
      report_ids: string[];
      reference: string;
    }) => {
      setIsPending(true);
      setError(null);
      try {
        await apiFetch(supabase, 'expenses/reports/reimburse-bulk', {
          method: 'POST',
          body: JSON.stringify(input),
        });
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setIsPending(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return { mutate, isPending, error };
}
